import {
  Component, EventEmitter, HostBinding, Input, OnChanges, Output, ViewEncapsulation, SimpleChanges, ChangeDetectionStrategy
} from '@angular/core';

import {
  BrandThemesEnum, ILinkMedia, ImageRatioEnum, IMedia, PositionEnum, SdBannerComponent, SdLinkComponent,
  SdThemeComponent, SdTitleComponent, ThemesEnum, ThemesTextEnum, ThemesUtils
} from '@seb/web-design-system/core';

@Component({
  selector: 'sd-wysiwyg',
  templateUrl: './wysiwyg.component.html',
  styleUrls: ['./wysiwyg.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdTitleComponent,
    SdLinkComponent,
    SdBannerComponent
  ]
})
export class SdWysiwygComponent extends SdThemeComponent implements OnChanges {
  @Input()
  isLoading = false;

  @Input()
  title: string;

  @Input()
  titleThemeConfig: ThemesEnum.dark | BrandThemesEnum.brand | ThemesEnum.light = BrandThemesEnum.brand;

  /**
   * Allow to define the theme for the content based on the parent's theme config
   * * @param {string} dark | light | promo-1 | promo-2 | stars | extra | brand
   */
  @Input()
  parentsThemesConfig: ThemesEnum = ThemesEnum.light;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel = 3;

  @Input()
  mediaTop: IMedia;

  @Input()
  mediaBottom: IMedia;

  @Input()
  ratioMediaTop: ImageRatioEnum.r16_9 | ImageRatioEnum.r4_3 | ImageRatioEnum.r1_1;

  @Input()
  ratioMediaBottom: ImageRatioEnum.r16_9 | ImageRatioEnum.r4_3 | ImageRatioEnum.r1_1;

  @Input()
  links: ILinkMedia[];

  /**
   * Allow to define if the text is aligned to the top or centered based of the medias
   * * @param {string} top | center
   */
  @Input()
  textConfig: PositionEnum.top | PositionEnum.center = PositionEnum.top;

  /**
   * Allow to define if the medias are on the right or on the left of the main content
   * * @param {string} right | right
   */
  @Input()
  mediaConfig: PositionEnum.left | PositionEnum.right = PositionEnum.right;

  @Input()
  twicPicsAutoSize = true;

  @Input()
  layout = '';

  @Output()
  linkClicked = new EventEmitter<ILinkMedia>();

  @Output()
  mediaPlay = new EventEmitter<{ videoName: string; title: string }>();

  @Input()
  class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.parentsThemesConfig ? 'has-parent-' + this.parentsThemesConfig?.toLowerCase() : '',
      this.contentThemesConfig ? 'is-theme-' + this.contentThemesConfig : '',
      this.textConfig ? 'is-text-align-' + this.textConfig : '',
      this.mediaConfig ? 'is-media-' + this.mediaConfig : '',
      !this.mediaTop && !this.mediaBottom ? 'has-no-media' : '',
      this.layout ? 'is-' + this.layout : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  manageThemesTextConfigUtils = ThemesUtils.manageThemesTextConfig;
  contentThemesConfig: ThemesTextEnum;

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    this.manageThemesTextConfig(
      this.parentsThemesConfig?.toLowerCase() as ThemesEnum,
    );
  }

  clicked(link: ILinkMedia): void {
    this.linkClicked.emit(link);
  }

  manageThemesTextConfig(theme: ThemesEnum): void {
    this.contentThemesConfig = this.manageThemesTextConfigUtils(theme);
  }

  onMediaPlay(media: IMedia, title: string): void {
    this.mediaPlay.emit({ videoName: media?.altText, title });
  }
}
